'use client';

/**
 * SLAIndicator Component
 *
 * A badge component that displays SLA status with visual indicators
 * for on_track, warning, and breached states.
 */

import Box from '@mui/material/Box';
import { styled, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type React from 'react';
import { type NeonAccent, neonAccents } from '../../theme/palette';
import { transitionPresets } from '../../theme/transitions';
import type { SLAIndicatorProps, SLAStatus } from '../../types/pipeline';
import { transientOptions } from '../../utils/styledUtils';

// ============================================================================
// Types
// ============================================================================

interface SLAStyleConfig {
  bg: string;
  border: string;
  text: string;
  glow: string;
}

// ============================================================================
// Styled Components
// ============================================================================

interface StyledBadgeProps {
  $status: SLAStatus;
  $size: 'small' | 'medium' | 'large';
  $isDark: boolean;
}

const getSLAColors = (status: SLAStatus, isDark: boolean): SLAStyleConfig => {
  const statusMap: Record<SLAStatus, NeonAccent> = {
    on_track: 'green',
    warning: 'amber',
    breached: 'red',
  };

  const accent = statusMap[status];
  const colors = neonAccents[accent];

  return {
    bg: isDark ? colors.bg : colors.bgLight,
    border: isDark ? colors.border : colors.borderLight,
    text: colors.base,
    glow: colors.glow,
  };
};

const StyledBadge = styled(
  Box,
  transientOptions,
)<StyledBadgeProps>(({ $status, $size, $isDark }) => {
  const colors = getSLAColors($status, $isDark);

  const sizeStyles = {
    small: {
      padding: '2px 6px',
      fontSize: '10px',
      borderRadius: '4px',
      gap: '3px',
    },
    medium: {
      padding: '4px 8px',
      fontSize: '11px',
      borderRadius: '6px',
      gap: '4px',
    },
    large: {
      padding: '6px 12px',
      fontSize: '13px',
      borderRadius: '8px',
      gap: '6px',
    },
  };

  const size = sizeStyles[$size];

  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: size.gap,
    padding: size.padding,
    borderRadius: size.borderRadius,
    background: colors.bg,
    border: `1px solid ${colors.border}`,
    transition: transitionPresets.allFast,

    '& .sla-dot': {
      width: $size === 'small' ? '6px' : $size === 'medium' ? '8px' : '10px',
      height: $size === 'small' ? '6px' : $size === 'medium' ? '8px' : '10px',
      borderRadius: '50%',
      background: colors.text,
      boxShadow: `0 0 6px ${colors.glow}`,
      animation: $status === 'breached' ? 'pulse-sla 1.5s ease-in-out infinite' : 'none',
    },

    '& .sla-text': {
      fontSize: size.fontSize,
      fontWeight: 600,
      color: colors.text,
      lineHeight: 1,
      textTransform: 'uppercase',
      letterSpacing: '0.02em',
    },

    '@keyframes pulse-sla': {
      '0%, 100%': {
        opacity: 1,
        transform: 'scale(1)',
      },
      '50%': {
        opacity: 0.6,
        transform: 'scale(1.2)',
      },
    },
  };
});

// ============================================================================
// Helper Functions
// ============================================================================

const formatTimeRemaining = (hours: number): string => {
  if (hours < 0) {
    const absHours = Math.abs(hours);
    if (absHours < 1) {
      return `-${Math.round(absHours * 60)}m`;
    }
    if (absHours < 24) {
      return `-${Math.round(absHours)}h`;
    }
    return `-${Math.round(absHours / 24)}d`;
  }

  if (hours < 1) {
    return `${Math.round(hours * 60)}m`;
  }
  if (hours < 24) {
    return `${Math.round(hours)}h`;
  }
  return `${Math.round(hours / 24)}d`;
};

const getStatusLabel = (status: SLAStatus): string => {
  const labels: Record<SLAStatus, string> = {
    on_track: 'On Track',
    warning: 'Warning',
    breached: 'Breached',
  };
  return labels[status];
};

// ============================================================================
// Component
// ============================================================================

export const SLAIndicator: React.FC<SLAIndicatorProps> = ({
  status,
  remainingHours,
  showCountdown = true,
  size = 'medium',
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  return (
    <StyledBadge $status={status} $size={size} $isDark={isDark}>
      <span className="sla-dot" />
      <Typography component="span" className="sla-text">
        {showCountdown ? formatTimeRemaining(remainingHours) : getStatusLabel(status)}
      </Typography>
    </StyledBadge>
  );
};

SLAIndicator.displayName = 'SLAIndicator';

export default SLAIndicator;
